import { TGetOrganizationListDto } from "@/api/services/admin/organization/dtos/get-organization-list.dto";
import { IconStar, IconStarOutline } from "@/assets/icons";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { TableSorting } from "@/components/elements/Table/TableSorting";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { TOrganizationFilterCache } from "@/features/organization/types/organizationFilter";
import { text } from "@/theme/colors";
import { EProductCode } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { IconButton, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";

type UseOrganizationListColumnsParams = {
  organizationTypes: { label: string; value: string }[] | undefined;
  togglePin: (id: number) => void;
  filterCache: TOrganizationFilterCache;
};

export const useOrganizationListColumns = ({ organizationTypes, togglePin, filterCache }: UseOrganizationListColumnsParams) => {
  const columnVisibility = useMemo(() => {
    const vc = filterCache.visibleColumns ?? {};
    return Object.fromEntries(Object.entries(vc).map(([key, val]) => [key, val !== false]));
  }, [filterCache.visibleColumns]);

  const columnOrderState = useMemo(() => filterCache.columnOrder ?? [], [filterCache.columnOrder]);

  const columns = useMemo<MRT_ColumnDef<TGetOrganizationListDto>[]>(
    () => [
      {
        accessorKey: "externalId",
        header: "組織ID",
        size: 130,
        Header: () => (
          <TableSorting name="externalId">
            <Typography variant="body14Bold" color={text.primary}>
              組織ID
            </Typography>
          </TableSorting>
        ),
      },
      {
        accessorKey: "name",
        header: "組織名",
        size: 260,
        Header: () => (
          <TableSorting name="name">
            <Typography variant="body14Bold" color={text.primary}>
              組織名
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => (
          <Stack flexDirection="row" gap={1} alignItems="center" minWidth={0}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                togglePin(row.original?.id);
              }}
            >
              {row.original?.isPinned ? <IconStar /> : <IconStarOutline />}
            </IconButton>
            <EllipsisText title={row.original?.name ?? EMPTY_STR.TEXT} variant="body14" maxWidth={200} />
          </Stack>
        ),
      },
      {
        accessorKey: "address",
        header: "住所",
        size: 116,
        Cell: ({ row }) => {
          const address = `${row.original?.provinceName ?? ""}${row.original?.municipalityName ?? ""}`;
          return address ? <EllipsisText title={address} variant="body14" maxWidth={96} /> : EMPTY_STR.TEXT;
        },
      },
      {
        accessorKey: "products",
        header: "契約サービス",
        size: 181,
        Header: () => (
          <Typography variant="body14Bold" color={text.primary}>
            契約サービス
          </Typography>
        ),
        Cell: ({ row }) => {
          // TODO: [match-phase] Restore EProductCode.MATCH in targetCodes when match integration is implemented.
          const targetCodes = [EProductCode.VISA, EProductCode.ACCOUNT, /* EProductCode.MATCH, */ EProductCode.STUDY];
          const products = targetCodes
            .map((code) => row.original?.productContracts?.find((c) => c.product.code === code)?.product)
            .filter((product): product is NonNullable<typeof product> => Boolean(product))
            .map((product) => ({ code: product.code, singleCharLabel: product.singleCharLabel, name: product.name }));
          return products.length > 0 ? <ContractServiceBadge products={products} /> : EMPTY_STR.TEXT;
        },
      },
      {
        id: "userCount",
        header: "登録ユーザー数",
        size: 130,
        Header: () => (
          <Typography variant="body14Bold" color={text.primary}>
            登録ユーザー数
          </Typography>
        ),
        Cell: ({ row }) => {
          return row.original?.userCount;
        },
      },
      {
        id: "lastLoginAt",
        header: "最終ログイン",
        size: 148,
        Header: () => (
          <TableSorting name="lastLoginAt">
            <Typography variant="body14Bold" color={text.primary}>
              最終ログイン
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const date = row.original?.lastLoginAt;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
      {
        accessorKey: "createdAt",
        header: "登録日",
        size: 106,
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              登録日
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const date = row.original?.createdAt;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
    ],
    [organizationTypes, togglePin],
  );

  return { columns, columnVisibility, columnOrderState };
};
